/**
 * B2EditLockManager — Sistema de Edición Consciente para B2 (Trabajo Grupal)
 * Solución C — 12 Mar 2026
 *
 * Garantiza que solo un estudiante edite B2 a la vez con consentimiento explícito.
 * Nada se edita por accidente. Todo lock es intencional y visible.
 *
 * Props:
 *   submissionId   — ID de la submission activa
 *   groupId        — ID del grupo
 *   weekNumber     — Número de semana
 *   currentUserId  — ID del usuario autenticado
 *   currentUserName— Nombre para mostrar en mensajes
 *   children       — Campos del formulario B2 (se bloquean visualmente cuando no se edita)
 *   onSave         — Callback para disparar guardado manual
 *   lastSaved      — Date | null: último guardado exitoso
 *   saving         — Boolean: guardado en progreso
 *   disabled       — Boolean: B2 cerrado (submission closed, no se puede solicitar edición)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Lock, Edit3, CheckCircle, Clock, Bell, X, AlertTriangle, Wifi } from 'lucide-react';
import apiClient from '../services/apiClient';

const B2EditLockManager = ({
  submissionId,
  groupId,
  weekNumber,
  currentUserId,
  currentUserName,
  children,
  onSave,
  onContentUpdate,  // Callback para refrescar contenido cuando otro usuario termina de editar
  lastSaved,
  saving,
  disabled = false,
  blockType = 'b2'  // 'b2' (predeterminado) o 'b5'
}) => {
  const [isEditing, setIsEditing]             = useState(false);
  const [lockStatus, setLockStatus]           = useState({ locked: false, yourLock: false, currentEditor: null });
  const [showReleaseToast, setShowReleaseToast] = useState(false);
  const [releaseRequest, setReleaseRequest]   = useState(null); // { _id, requesterName, message }
  const [requestingEdit, setRequestingEdit]   = useState(false);
  const [requestingRelease, setRequestingRelease] = useState(false);
  const [releaseRequestSent, setReleaseRequestSent] = useState(false);
  const [pingError, setPingError]             = useState(false); // heartbeat falló → lock expiró

  // Ref para detectar transición: otro editaba → ahora libre → refrescar contenido
  const prevLockRef = useRef({ locked: false, yourLock: false });
  // Timestamp del último contenido conocido — si el servidor devuelve uno más nuevo, refrescamos
  const lastContentTsRef = useRef(null);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers API
  // ─────────────────────────────────────────────────────────────────────────────

  const base = `/student-execution/groups/${groupId}/week/${weekNumber}`;

  const checkEditStatus = useCallback(async () => {
    if (!submissionId || !groupId) return;
    try {
      // _t fuerza una URL única en cada poll — evita que Edge/Chrome cacheen la respuesta GET
      const res = await apiClient.get(`${base}/edit-status`, { params: { _t: Date.now(), blockType } });
      const data = res.data;

      const prev = prevLockRef.current;

      // Método 1 (edge): otro compañero tenía el lock y acaba de liberarlo
      const otherJustReleased = prev.locked && !prev.yourLock && !data.locked;

      // Detectar si NOSOTROS acabamos de liberar nuestro propio lock.
      // En ese caso ya guardamos el contenido correcto con forceSaveWork — NO refrescar del servidor
      // (el fetch del servidor podría traer datos del último autosave, más viejos que lo que acabamos de guardar)
      const weJustReleased = prev.yourLock && !data.yourLock;

      // Método 2 (timestamp): el servidor reporta un contenido más nuevo que el que tenemos.
      // Solo aplica cuando fue OTRO quien editó, no nosotros mismos.
      let contentChanged = false;
      if (!isEditing && !weJustReleased && data.contentUpdatedAt) {
        const serverTs = new Date(data.contentUpdatedAt).getTime();
        if (lastContentTsRef.current === null) {
          // Primera vez: solo guardar el timestamp, no refrescar todavía
          lastContentTsRef.current = serverTs;
        } else if (serverTs > lastContentTsRef.current) {
          contentChanged = true;
          lastContentTsRef.current = serverTs;
        }
      }

      // Si nosotros liberamos: avanzar el timestamp de referencia sin hacer fetch,
      // para que futuros polls no detecten un "cambio" y sobreescriban el estado local
      if (weJustReleased && data.contentUpdatedAt) {
        lastContentTsRef.current = new Date(data.contentUpdatedAt).getTime();
      }

      if ((otherJustReleased || contentChanged) && onContentUpdate) {
        onContentUpdate();
      }

      prevLockRef.current = { locked: data.locked, yourLock: data.yourLock };

      setLockStatus({
        locked: data.locked,
        yourLock: data.yourLock,
        currentEditor: data.currentEditor || null
      });
      // Si yo tenía el lock pero ya no aparece como mío (expiró por TTL)
      if (isEditing && !data.yourLock) {
        setIsEditing(false);
        setPingError(true);
      }
    } catch (_e) {
      // Silencioso — errores de red no deben romper la UI
    }
  }, [base, submissionId, groupId, isEditing, onContentUpdate]);

  const sendHeartbeat = useCallback(async () => {
    if (!isEditing) return;
    try {
      const res = await apiClient.post(`${base}/ping-edit`, { blockType });
      if (!res.data.extended) {
        // Lock expiró antes del heartbeat
        setIsEditing(false);
        setPingError(true);
      }
    } catch (_e) {
      // Si el ping falla, el TTL se encargará en 2 min
    }
  }, [base, isEditing]);

  const checkIncomingNotifications = useCallback(async () => {
    if (!isEditing) return;
    try {
      const res = await apiClient.get('/student-execution/my-edit-notifications', { params: { blockType } });
      const notifs = res.data.notifications || [];
      if (notifs.length > 0 && !showReleaseToast) {
        setReleaseRequest(notifs[0]); // Mostrar la primera pendiente
        setShowReleaseToast(true);
      }
    } catch (_e) {
      // Silencioso
    }
  }, [isEditing, showReleaseToast]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Intervals
  // ─────────────────────────────────────────────────────────────────────────────

  const statusIntervalRef    = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const notifIntervalRef     = useRef(null);

  useEffect(() => {
    if (!submissionId) return;

    // Status poll: siempre activo cada 5s
    statusIntervalRef.current = setInterval(checkEditStatus, 5000);
    checkEditStatus(); // Poll inmediato al montar

    return () => clearInterval(statusIntervalRef.current);
  }, [checkEditStatus]);

  useEffect(() => {
    clearInterval(heartbeatIntervalRef.current);
    clearInterval(notifIntervalRef.current);

    if (isEditing) {
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, 30000);
      notifIntervalRef.current     = setInterval(checkIncomingNotifications, 5000);
    }

    return () => {
      clearInterval(heartbeatIntervalRef.current);
      clearInterval(notifIntervalRef.current);
    };
  }, [isEditing, sendHeartbeat, checkIncomingNotifications]);

  // Liberar lock automáticamente si el usuario cierra/abandona la página
  useEffect(() => {
    const releaseLockOnUnload = async () => {
      if (isEditing) {
        try {
          await apiClient.post(`${base}/release-edit`, { blockType });
        } catch (_e) { /* el TTL lo limpia */ }
      }
    };
    window.addEventListener('beforeunload', releaseLockOnUnload);
    return () => window.removeEventListener('beforeunload', releaseLockOnUnload);
  }, [isEditing, base, blockType]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Acciones
  // ─────────────────────────────────────────────────────────────────────────────

  const handleRequestEdit = async () => {
    if (disabled || requestingEdit) return;
    setRequestingEdit(true);
    setPingError(false);
    try {
      const res = await apiClient.post(`${base}/request-edit`, {
        deviceType: isMobile ? 'mobile' : 'desktop',
        blockType
      });
      if (res.data.granted) {
        setIsEditing(true);
        setLockStatus({ locked: true, yourLock: true, currentEditor: res.data.expiresAt ? {
          expiresAt: res.data.expiresAt
        } : null });
      } else {
        // Sin lock disponible → actualizar status y mostrar modal
        setLockStatus({
          locked: true,
          yourLock: false,
          currentEditor: res.data.currentEditor
        });
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setLockStatus({
          locked: true,
          yourLock: false,
          currentEditor: err.response.data.currentEditor
        });
      }
    } finally {
      setRequestingEdit(false);
    }
  };

  const handleFinishEditing = async () => {
    if (onSave) await onSave();
    try {
      await apiClient.post(`${base}/release-edit`, { blockType });
    } catch (_e) { /* ignorar */ }
    setIsEditing(false);
    setLockStatus({ locked: false, yourLock: false, currentEditor: null });
    setReleaseRequestSent(false);
  };

  const handleRequestRelease = async () => {
    if (requestingRelease || releaseRequestSent) return;
    setRequestingRelease(true);
    try {
      await apiClient.post(`${base}/request-release`, {
        message: blockType === 'b5'
          ? 'Necesito redactar el resumen B5'
          : 'Necesito editar el formulario grupal',
        blockType
      });
      setReleaseRequestSent(true);
    } catch (_e) {
      // Si el lock ya no existe, refrescar status
      await checkEditStatus();
    } finally {
      setRequestingRelease(false);
    }
  };

  const handleRespondToRelease = async (action) => {
    if (!releaseRequest) return;
    try {
      await apiClient.post(`/student-execution/edit-notifications/${releaseRequest._id}/resolve`, { action });
    } catch (_e) { /* ignorar */ }

    setShowReleaseToast(false);
    setReleaseRequest(null);

    if (action === 'released') {
      await handleFinishEditing();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UI helpers
  // ─────────────────────────────────────────────────────────────────────────────

  const secondsAgo = lockStatus.currentEditor?.secondsSinceActivity ?? null;
  const editorName = lockStatus.currentEditor?.name ?? 'un compañero';

  const formatSecondsAgo = (s) => {
    if (s === null) return '';
    if (s < 60)  return `hace ${s}s`;
    if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
    return `hace más de 1h`;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="relative">

      {/* ── BANNER DE ESTADO ── */}
      {!disabled && (
        <div className="mb-4">

          {/* Estado A: LIBRE */}
          {!lockStatus.locked && !isEditing && !pingError && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 text-blue-700 text-sm">
                <Lock className="w-4 h-4 shrink-0" />
                <span>Formulario protegido. Solicita acceso para editar.</span>
              </div>
              <button
                onClick={handleRequestEdit}
                disabled={requestingEdit}
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors min-h-[44px] sm:min-w-[180px]"
              >
                {requestingEdit ? (
                  <><Clock className="w-4 h-4 animate-spin" /><span>Solicitando...</span></>
                ) : (
                  <><Edit3 className="w-4 h-4" /><span>Solicitar Edición</span></>
                )}
              </button>
            </div>
          )}

          {/* Estado A': Lock expiró mientras editaba */}
          {pingError && !isEditing && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-orange-50 border border-orange-300 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 text-orange-700 text-sm">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Tu sesión de edición expiró. Vuelve a solicitar acceso.</span>
              </div>
              <button
                onClick={handleRequestEdit}
                disabled={requestingEdit}
                className="flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors min-h-[44px]"
              >
                <Edit3 className="w-4 h-4" />
                <span>Solicitar de nuevo</span>
              </button>
            </div>
          )}

          {/* Estado B: OTRO EDITA */}
          {lockStatus.locked && !lockStatus.yourLock && !isEditing && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3 space-y-3">
              <div className="flex items-center gap-2 text-yellow-800 text-sm">
                <Wifi className="w-4 h-4 shrink-0" />
                <span>
                  <strong>{editorName}</strong> está editando
                  {secondsAgo !== null && ` — ${formatSecondsAgo(secondsAgo)}`}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={checkEditStatus}
                  className="flex items-center justify-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-lg transition-colors min-h-[44px]"
                >
                  <Clock className="w-4 h-4" />
                  <span>Actualizar estado</span>
                </button>
                <button
                  onClick={handleRequestRelease}
                  disabled={requestingRelease || releaseRequestSent}
                  className="flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors min-h-[44px]"
                >
                  <Bell className="w-4 h-4" />
                  <span>
                    {releaseRequestSent
                      ? 'Solicitud enviada'
                      : requestingRelease
                      ? 'Enviando...'
                      : `Solicitar a ${editorName}`}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Estado C: YO EDITO */}
          {isEditing && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-green-50 border border-green-300 rounded-lg px-4 py-3">
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                  <Edit3 className="w-4 h-4 shrink-0" />
                  <span>Estás editando. Presiona "Terminar" cuando acabes.</span>
                </div>
                <div className="flex items-center gap-2 text-green-600 text-xs">
                  {saving && <><Clock className="w-3 h-3 animate-pulse" /><span>Guardando...</span></>}
                  {lastSaved && !saving && <><CheckCircle className="w-3 h-3" /><span>Guardado {lastSaved.toLocaleTimeString()}</span></>}
                </div>
              </div>
              <button
                onClick={handleFinishEditing}
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors min-h-[44px] sm:min-w-[160px]"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Terminar Edición</span>
              </button>
            </div>
          )}

        </div>
      )}

      {/* ── CAMPOS DEL FORMULARIO ── */}
      <div className="relative">
        {/* Overlay que bloquea interacción cuando no se está editando */}
        {!disabled && !isEditing && (
          <div
            className="absolute inset-0 z-10 cursor-not-allowed rounded-lg"
            onClick={handleRequestEdit}
            title="Haz clic en 'Solicitar Edición' para poder editar"
          />
        )}
        <div className={(!disabled && !isEditing) ? 'opacity-60 select-none' : ''}>
          {children}
        </div>
      </div>

      {/* ── TOAST DESKTOP / BOTTOM-SHEET MOBILE: Solicitud de liberación entrante ── */}
      {showReleaseToast && releaseRequest && (
        isMobile ? (
          // ── MOBILE: pantalla completa con bottom sheet animado ──
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
            <div className="bg-white rounded-t-3xl w-full p-6 animate-slide-up" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
              {/* Handle de arrastre */}
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />

              {/* Cabecera centrada */}
              <div className="text-center mb-4">
                <Bell className="w-16 h-16 text-yellow-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-gray-900">Solicitud de Edición</h2>
              </div>

              {/* Cuerpo */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700">
                  <strong className="text-yellow-800">{releaseRequest.requesterName}</strong> necesita editar el formulario y te solicita que liberes.
                </p>
                {releaseRequest.message && (
                  <p className="text-xs text-gray-500 italic mt-1">"{releaseRequest.message}"</p>
                )}
              </div>

              {/* Botones grandes */}
              <button
                onClick={() => handleRespondToRelease('released')}
                className="w-full py-4 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-xl text-base font-semibold mb-3 transition-colors"
              >
                ✓ Sí, liberar ahora
              </button>
              <button
                onClick={() => handleRespondToRelease('ignored')}
                className="w-full py-4 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-gray-700 rounded-xl text-base transition-colors"
              >
                Seguir editando (2 minutos más)
              </button>
            </div>
          </div>
        ) : (
          // ── DESKTOP: toast esquina superior derecha ──
          <div className="fixed top-6 right-6 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 w-80 p-4 animate-slide-in">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-yellow-500" />
                <span className="text-sm font-semibold text-gray-900">Solicitud de acceso</span>
              </div>
              <button
                onClick={() => setShowReleaseToast(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-gray-700 mb-1">
              <strong>{releaseRequest.requesterName}</strong> necesita editar el formulario grupal.
            </p>
            {releaseRequest.message && (
              <p className="text-xs text-gray-500 italic mb-4">"{releaseRequest.message}"</p>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => handleRespondToRelease('released')}
                className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 rounded-lg transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Liberar
              </button>
              <button
                onClick={() => handleRespondToRelease('ignored')}
                className="flex-1 flex items-center justify-center gap-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium py-2 rounded-lg transition-colors"
              >
                Seguir editando
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default B2EditLockManager;
